import { useMemo, useState } from "react";
import { InitialsAvatar } from "../../components/InitialsAvatar";
import { Link } from "react-router-dom";
import { useResource, titleCase, daysUntil } from "../../lib/hooks";
import { FilterBar, matches } from "../../components/FilterBar";
import type { CompetencyType, Employee, TrainingRecord } from "../../lib/types";

/**
 * The CRM's staff directory — a read-oriented view of the same workforce the
 * ShiftFlow Employees module manages. Editing stays over there, deliberately:
 * one write surface per record keeps the audit story simple, and this page is
 * for the manager who is in CRM mode and needs to check a competency before
 * committing to a lead.
 */
export const Staff = () => {
    const employees = useResource<Employee[]>("/employees");
    const training = useResource<TrainingRecord[]>("/training-records");
    const competencies = useResource<CompetencyType[]>("/competency-types");

    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<Record<string, string>>({});

    const competencyName = useMemo(() => {
        const map = new Map((competencies.data ?? []).map((c) => [c.id, c.name]));
        return (id: string) => map.get(id) ?? "Unknown";
    }, [competencies.data]);

    const complianceFor = (employeeId: string) => {
        const records = (training.data ?? []).filter((t) => t.employee_id === employeeId);
        const expired = records.filter((t) => t.expiry_date && daysUntil(t.expiry_date) < 0);
        const dueSoon = records.filter((t) => t.expiry_date && daysUntil(t.expiry_date) >= 0 && daysUntil(t.expiry_date) <= 30);
        return { expired, dueSoon };
    };

    const all = employees.data ?? [];
    const filtered = all.filter((e) =>
        matches(search, e.full_name, e.email) &&
        (!filters.employment_status || e.employment_status === filters.employment_status)
    );

    if (employees.error) return <div className="error-banner">{employees.error}</div>;

    return (
        <div>
            <FilterBar
                search={search} onSearch={setSearch}
                searchPlaceholder="Search staff…"
                values={filters}
                onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value }))}
                filters={[{
                    name: "employment_status", allLabel: "All Status",
                    options: ["active", "on_leave", "inactive", "terminated"].map((s) => ({ value: s, label: titleCase(s) })),
                }]}
                action={
                    <Link className="btn ghost" to="/employees">
                        Manage in ShiftFlow →
                    </Link>
                }
            />

            <div className="card">
                <div className="card-body flush">
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr><th>Staff member</th><th>Employment</th><th>Status</th><th>Compliance</th></tr>
                            </thead>
                            <tbody>
                                {filtered.map((employee) => {
                                    const compliance = complianceFor(employee.id);
                                    return (
                                        <tr key={employee.id}>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                                                    <InitialsAvatar name={employee.full_name} size={28} />
                                                    <div>
                                                        <div style={{ fontWeight: 600 }}>{employee.full_name}</div>
                                                        <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>{employee.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{titleCase(employee.employment_type)}</td>
                                            <td>
                                                <span className={`chip ${employee.employment_status === "active" ? "pass" : "mute"}`}>
                                                    {titleCase(employee.employment_status)}
                                                </span>
                                            </td>
                                            <td>
                                                {compliance.expired.length > 0 ? (
                                                    <span className="chip block" title={compliance.expired.map((t) => competencyName(t.competency_type_id)).join(", ")}>
                                                        {compliance.expired.length} expired
                                                    </span>
                                                ) : compliance.dueSoon.length > 0 ? (
                                                    <span className="chip warn">
                                                        {compliance.dueSoon.length} due soon
                                                    </span>
                                                ) : (
                                                    <span className="chip pass">current</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="empty">
                                            {all.length === 0
                                                ? "Your role does not include workforce records."
                                                : "No staff match."}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};
